'use client'

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Settings,
  CreditCard,
  TrendingUp,
  Receipt,
  FileText,
  BarChart3,
  X,
  User,
  Tag,
  QrCode,
  Warehouse,
  Diamond,
  Coins,
  ChevronLeft,
  ChevronRight,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn, isActivePathNew as isActivePath } from "@/lib/utils";
import { useMode } from "@/contexts/ModeContext";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
}

const jewelryNavItems: NavItem[] = [
  {
    label: "Dashboard",
    path: "/",
    icon: LayoutDashboard,
    description: "Overview and analytics",
  },
  {
    label: "Products",
    path: "/products",
    icon: Package,
    description: "Manage inventory",
  },
  {
    label: "Stock Management",
    path: "/stock-management",
    icon: Warehouse,
    description: "Stock overview and analysis",
  },
  {
    label: "Scan QR",
    path: "/qr-scanner",
    icon: QrCode,
    description: "Scan product QR codes",
  },
  {
    label: "Categories",
    path: "/categories",
    icon: Tag,
    description: "Manage categories",
  },
  {
    label: "Customers",
    path: "/customers",
    icon: Users,
    description: "Customer database",
  },
  {
    label: "Transactions",
    path: "/transactions",
    icon: CreditCard,
    description: "Payment & advance history",
  },
  {
    label: "Reports",
    path: "/reports",
    icon: BarChart3,
    description: "Analytics & insights",
  },
  {
    label: "Invoicing",
    path: "/invoicing",
    icon: FileText,
    description: "Create invoices",
  },
  {
    label: "POS",
    path: "/pos",
    icon: Receipt,
    description: "Point of sale",
  },
  {
    label: "Advance Payments",
    path: "/advance-payments",
    icon: Wallet,
    description: "Manage advance payments",
  },
  {
    label: "Profile",
    path: "/profile",
    icon: User,
    description: "Account settings",
  },
];

const pawnNavItems: NavItem[] = [
  {
    label: "Pawn Dashboard",
    path: "/pawn",
    icon: LayoutDashboard,
    description: "Overview and analytics",
  },
  {
    label: "Active Loans",
    path: "/pawn/loans",
    icon: Coins,
    description: "Manage active loans",
  },
  {
    label: "New Loan",
    path: "/pawn/loans/new",
    icon: CreditCard,
    description: "Create new loan",
  },
  {
    label: "Customers",
    path: "/pawn/customers",
    icon: Users,
    description: "Manage pawn customers",
  },
  {
    label: "Payments",
    path: "/pawn/payments",
    icon: Receipt,
    description: "Payment history",
  },
  {
    label: "Pledged Items",
    path: "/pawn/items",
    icon: Package,
    description: "Manage pledged items",
  },
  {
    label: "Loan History",
    path: "/pawn/history",
    icon: FileText,
    description: "View loan history",
  },
  {
    label: "Reports",
    path: "/pawn/reports",
    icon: BarChart3,
    description: "Analytics & insights",
  },
  {
    label: "Profile",
    path: "/profile",
    icon: User,
    description: "Account settings",
  },
];

export const SidePanel: React.FC<SidePanelProps> = ({ isOpen, onClose }) => {
  const [mounted, setMounted] = useState(false);
  const [pathname, setPathname] = useState<string>('');
  
  // Only call usePathname after mounting to prevent SSR issues
  const pathnameHook = usePathname();
  const { appMode, setAppMode } = useMode();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navItems = appMode === 'PAWN' ? pawnNavItems : jewelryNavItems;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      setPathname(pathnameHook);
      console.log('SidePanel - Current pathname:', pathnameHook);
    }
  }, [pathnameHook, mounted]);

  // Always render the aside element to prevent hydration mismatch
  // The visibility will be controlled by CSS classes

  return (
    <TooltipProvider>
      <aside className="no-print">
        {/* Backdrop */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={onClose}
          />
        )}

        {/* Side Panel */}
        <div
          className={cn(
            "fixed top-0 left-0 h-full bg-white dark:bg-luxury-charcoal-800 border-r border-luxury-charcoal-200 dark:border-luxury-charcoal-700 z-50 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto flex flex-col",
            isCollapsed ? "w-16" : "w-80",
            isOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-4 border-b border-luxury-charcoal-200 dark:border-luxury-charcoal-700 flex-shrink-0">
            {!isCollapsed && (
              <div>
                <h1 className="text-xl font-bold text-luxury-charcoal-900 dark:text-white">
                  SaarthiQ v1.0
                </h1>
                <p className="text-sm text-luxury-charcoal-600 dark:text-luxury-charcoal-400">
                  Navigation Menu
                </p>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="lg:hidden"
              >
                <X className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="hidden lg:flex"
              >
                {isCollapsed ? (
                  <ChevronRight className="w-4 h-4" />
                ) : (
                  <ChevronLeft className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Mode Switcher */}
          <div className="p-4 border-b border-luxury-charcoal-200 dark:border-luxury-charcoal-700">
            {isCollapsed ? (
              <div className="flex flex-col gap-2">
                <Button
                  variant={appMode === 'JEWELRY' ? 'default' : 'outline'}
                  size="sm"
                  className="w-full h-8"
                  onClick={() => setAppMode('JEWELRY')}
                  title="Jewelry Mode"
                >
                  <Diamond className="w-4 h-4" />
                </Button>
                <Button
                  variant={appMode === 'PAWN' ? 'default' : 'outline'}
                  size="sm"
                  className="w-full h-8"
                  onClick={() => setAppMode('PAWN')}
                  title="Pawn Mode"
                >
                  <Coins className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant={appMode === 'JEWELRY' ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1"
                  onClick={() => setAppMode('JEWELRY')}
                >
                  <Diamond className="w-4 h-4 mr-2" />
                  Jewelry
                </Button>
                <Button
                  variant={appMode === 'PAWN' ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1"
                  onClick={() => setAppMode('PAWN')}
                >
                  <Coins className="w-4 h-4 mr-2" />
                  Pawn
                </Button>
              </div>
            )}
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActivePath(pathname, item.path);
              
              // Debug logging for pawn dashboard
              if (item.path === '/pawn') {
                console.log('Pawn Dashboard Debug:', {
                  pathname,
                  targetPath: item.path,
                  isActive,
                  cleanCurrentPath: pathname.split('?')[0].replace(/\/$/, ''),
                  cleanTargetPath: item.path.replace(/\/$/, '')
                });
              }

              return isCollapsed ? (
                <Tooltip key={item.path} delayDuration={200}>
                  <TooltipTrigger asChild>
                    <Link
                      href={item.path}
                      onClick={onClose}
                      className={cn(
                        "flex items-center justify-center px-0 py-3 rounded-xl transition-all duration-200 group",
                        isActive
                          ? "bg-luxury-gold-50 dark:bg-luxury-gold-900/20 text-luxury-gold-700 dark:text-luxury-gold-300 border border-luxury-gold-200 dark:border-luxury-gold-700"
                          : "text-luxury-charcoal-600 dark:text-luxury-charcoal-400 hover:bg-luxury-charcoal-50 dark:hover:bg-luxury-charcoal-700 hover:text-luxury-charcoal-900 dark:hover:text-white"
                      )}
                    >
                      <Icon
                        className={cn(
                          "w-5 h-5 transition-colors duration-200",
                          isActive
                            ? "text-luxury-gold-600 dark:text-luxury-gold-400"
                            : "text-luxury-charcoal-500 dark:text-luxury-charcoal-500 group-hover:text-luxury-charcoal-700 dark:group-hover:text-luxury-charcoal-300"
                        )}
                      />
                      {isActive && (
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-luxury-gold-500 dark:bg-luxury-gold-400 rounded-full" />
                      )}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="select-none">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Link
                  key={item.path}
                  href={item.path}
                  onClick={onClose}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group",
                    isActive
                      ? "bg-luxury-gold-50 dark:bg-luxury-gold-900/20 text-luxury-gold-700 dark:text-luxury-gold-300 border border-luxury-gold-200 dark:border-luxury-gold-700"
                      : "text-luxury-charcoal-600 dark:text-luxury-charcoal-400 hover:bg-luxury-charcoal-50 dark:hover:bg-luxury-charcoal-700 hover:text-luxury-charcoal-900 dark:hover:text-white"
                  )}
                >
                  <Icon
                    className={cn(
                      "w-5 h-5 transition-colors duration-200",
                      isActive
                        ? "text-luxury-gold-600 dark:text-luxury-gold-400"
                        : "text-luxury-charcoal-500 dark:text-luxury-charcoal-500 group-hover:text-luxury-charcoal-700 dark:group-hover:text-luxury-charcoal-300"
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{item.label}</div>
                    {item.description && (
                      <div className="text-xs text-luxury-charcoal-500 dark:text-luxury-charcoal-500 truncate">
                        {item.description}
                      </div>
                    )}
                  </div>
                  {isActive && (
                    <div className="w-2 h-2 bg-luxury-gold-500 dark:bg-luxury-gold-400 rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Footer - Fixed */}
          <div className="p-4 border-t border-luxury-charcoal-200 dark:border-luxury-charcoal-700 flex-shrink-0">
            {!isCollapsed && (
              <div className="text-center">
                <p className="text-xs text-luxury-charcoal-500 dark:text-luxury-charcoal-500">
                  SaarthiQ v1.0
                </p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
};

export default SidePanel; 